import { useEffect, useMemo, useState } from "react";
import { object, string, func } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { useEstimateStructure } from "@tools";
import { Accordion, Button, Checkbox, InfoLabel } from "@components";
import { SectionCheckbox } from "./components/SectionCheckbox";
import Grid from "@material-ui/core/Grid";
import { Visibility } from "@material-ui/icons";
import { WORK_TYPE_SETTINGS } from "@tools/EstimateEditor/utils";

export const DisplaySettings = observer(({
  workType,
  expandedAccordion,
  onToggle,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  
  const [ changes, setChanges ] = useState([]);
  
  const { id, name } = workType;

  const allVisible = useMemo(() => (
    !changes.find(s => s.exists && !s.newState)
  ), [ changes ]);
  
  const expanded = useMemo(() => (
    expandedAccordion === WORK_TYPE_SETTINGS.DISPLAY
  ), [ expandedAccordion ]);
  
  useEffect(() => {
    if(!expanded)
      return;
    
    resetSettings();
  }, [ expanded ]);
  
  const resetSettings = () => {
    setChanges(
      structure.sections
        .map(section => {
          const sectionWorkType = section.workTypes.find(wT => wT.id === id);
          const exists = Boolean(sectionWorkType);
          const state = !sectionWorkType?.invisible;
          return {
            id: section.id,
            exists,
            addToSection: false,
            name: section.name,
            state: state && exists,
            newState: state && exists
          };
        })
    );
  }

  const handleToggle = () => onToggle(WORK_TYPE_SETTINGS.DISPLAY, true);
  const handleClose = () => onToggle(WORK_TYPE_SETTINGS.DISPLAY);

  const handleReset = () => {
    if(!allVisible)
      structure.setWorkTypeVisibility(id, false, true);
    handleClose();
  };
  
  const handleChange = (newState, sectionId) => {
    setChanges(changes => {
      changes.forEach(s => {
        if(s.exists && !sectionId || s.id === sectionId)
          s.newState = newState;
      });
      return [...changes];
    })
  }

  const saveDisplaySettings = () => {
    handleClose();
    structure.historyManager.startGroup();
    const actualChanges = changes.filter(s => s.state !== s.newState || s.addToSection);
    actualChanges.forEach(s => {
      if(s.addToSection)
        structure.sections
          ?.find(section => section.id === s.id)
          ?.addWorkType(workType.id);
      else
        structure.sections
          ?.find(section => section.id === s.id)
          ?.setWorkTypeVisibility(id, !s.newState);
    });
    if(actualChanges?.find(s => s.newState))
      workType.resetFixedValues();
    structure.historyManager.stopGroup();
  }

  const handleAddToSection = (sectionId) => {
    setChanges(changes => {
      changes.forEach(s => {
        if(s.id === sectionId) {
          s.exists = true;
          s.addToSection = true;
          s.state = true;
          s.newState = true;
        }
      });
      return [...changes];
    })
  }
  
  return (
    <Accordion
      id="display-settings"
      title={<>
        <Visibility color="primary" className="text-lg" />
        <h3 className="ml-2">
          {t("views.editor.dialogs.work_type_settings.display.title")}
        </h3>
      </>}
      expanded={expanded}
      onExpand={handleToggle}
      titleColored
      allowOverflow
    >
      <Grid item container direction="column" className="p-4 gap-2">
        <InfoLabel label={ t("views.editor.dialogs.work_type_settings.display.subtitle", { name }) } translate={false} />
        <Checkbox
          name="selectAll"
          checked={allVisible}
          label={t("common.all_sections")}
          onChange={() => handleChange(!allVisible)}
          labelClassName="font-bold"
        />
        {
          changes.map(section => (
            <SectionCheckbox
              key={section.id}
              section={section}
              workTypeName={workType.name}
              onChange={() => handleChange(!section.newState, section.id)}
              onWorkTypeAdd={() => handleAddToSection(section.id)}
            />
          ))
        }
        
        <Grid item container justifyContent="flex-end" className="mt-4 gap-2">
          <Button
            variant="outlined"
            className="w-max"
            onClick={handleToggle}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="outlined"
            className="w-max"
            onClick={handleReset}
          >
            {t("views.editor.dialogs.display.reset")}
          </Button>
          <Button
            variant="contained"
            className="w-max"
            onClick={saveDisplaySettings}
          >
            {t("common.apply")}
          </Button>
        </Grid>
      </Grid>
    </Accordion>
  );
});

DisplaySettings.propTypes = {
  workType: object.isRequired,
  onToggle: func.isRequired,
  expandedAccordion: string,
};